import React, { useEffect } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { BreadCrumb } from '@helpcenter/bread-crumb-dumb';
import { HTTP } from '@helpcenter/constants';
import { DocumentTitle } from '@helpcenter/document-title';
import { ErrorPage } from '@helpcenter/error-page';
import { useInView } from 'react-intersection-observer';
import { usePaginationFragment, graphql } from 'react-relay';
import { PaperHeader } from 'view/common/paper-header';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsContext, AnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import { ExperienceSuccess } from '@atlassian/ufo';
import HelpObject from '../help-object';
import type { helpCenterTopic_HelpCenterQuery$key } from './__generated__/helpCenterTopic_HelpCenterQuery.graphql';
import type { HelpCenterTopicQuery } from './__generated__/HelpCenterTopicQuery.graphql';
import { topicViewExperience } from './experiences';
import type { Props } from './types';

export const HelpCenterTopic = (props: Props & WithAnalyticsEventsProps): JSX.Element | null => {
    const { loadNext, data, hasNext, isLoadingNext } = usePaginationFragment<
        HelpCenterTopicQuery,
        helpCenterTopic_HelpCenterQuery$key
    >(
        graphql`
            fragment helpCenterTopic_HelpCenterQuery on Query
            @argumentDefinitions(count: { type: "Int", defaultValue: 10 }, cursor: { type: "String" })
            @refetchable(queryName: "HelpCenterTopicQuery") {
                helpCenter(cloudId: $cloudId) {
                    helpCenterTopicById(helpCenterAri: $helpCenterAri, topicId: $topicId)
                        @optIn(to: ["HelpCenterAggBeta"]) {
                        ... on HelpCenterTopic {
                            name
                            description
                            items(first: $count, after: $cursor)
                                @connection(key: "mainHelpObjectsList_HelpCenterTopic_items") {
                                edges {
                                    node {
                                        ari
                                        data {
                                            ...helpObject_HelpCenterHelpObject
                                        }
                                    }
                                }
                            }
                        }
                        ... on QueryError {
                            __typename
                            message
                            extensions {
                                statusCode
                            }
                        }
                    }
                }
            }
        `,
        props.helpCenter
    );
    const { ref, inView } = useInView({
        threshold: 0,
    });

    useEffect(() => {
        if (hasNext && inView) {
            loadNext(10);
        }
    });

    useEffect(() => {
        if (data.helpCenter?.helpCenterTopicById?.__typename === 'QueryError') {
            const extensions = data.helpCenter?.helpCenterTopicById.extensions;
            const message = data.helpCenter?.helpCenterTopicById?.message;
            if (extensions && Array.isArray(extensions) && extensions.length > 0 && message) {
                // Suppressing existing violation. Please fix this.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const { statusCode } = extensions[0];
                // experience should not fail on 4xx errors
                const fetchError = new FetchError(message, statusCode);
                if (!isFetchNetworkOrClientError(fetchError)) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    topicViewExperience.failure({
                        metadata: {
                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            statusCode,
                            message,
                        },
                    });
                }
            }
        }
    }, [data]);

    const helpCenterTopic = data.helpCenter?.helpCenterTopicById;

    const error =
        data.helpCenter?.helpCenterTopicById?.__typename === 'QueryError'
            ? {
                  status: HTTP.SERVER,
                  message: '',
                  callToActionUrl: '',
                  callToActionText: '',
              }
            : undefined;

    const { createAnalyticsEvent } = props;
    React.useEffect(() => {
        helpCenterTopic?.items?.edges?.forEach((o) => {
            if (!o?.node?.data) {
                if (createAnalyticsEvent) {
                    const analyticsEvent = createAnalyticsEvent({
                        analyticsType: OPERATIONAL_EVENT_TYPE,
                        action: 'invalidHelpObjectFound',
                        actionSubject: 'helpObject',
                    });
                    if (analyticsEvent) {
                        analyticsEvent.fire();
                    }
                }
            }
        });
    }, [createAnalyticsEvent, helpCenterTopic]);

    return helpCenterTopic ? (
        <ErrorPage error={error}>
            <DocumentTitle title={data?.helpCenter?.helpCenterTopicById?.name || ''}>
                <>
                    <PaperHeader
                        subHeading={helpCenterTopic.name || ''}
                        description={helpCenterTopic.description || ''}
                        additionalBreadCrumbs={[
                            <BreadCrumb
                                to={`/topic/${props.id}`}
                                name={helpCenterTopic.name || ''}
                                actionSubjectId="topicBreadcrumb"
                                key={0}
                            />,
                        ]}
                    />
                    <List>
                        {helpCenterTopic.items?.edges?.map((edge, index) =>
                            edge?.node?.data ? (
                                <Item key={edge.node.ari}>
                                    <AnalyticsContext
                                        data={{
                                            attributes: {
                                                helpObjectPosition: index + 1,
                                            },
                                        }}
                                    >
                                        <HelpObject helpObject={edge.node.data} />
                                    </AnalyticsContext>
                                </Item>
                            ) : null
                        )}
                    </List>
                    <FireScreenEventOnMount />
                    <ExperienceSuccess experience={topicViewExperience} />
                    {/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/enforce-style-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve */}
                    <div className="pagination-sentinel" style={{ height: '20px' }} ref={ref}></div>
                    {isLoadingNext && <Spinner size="medium" />}
                </>
            </DocumentTitle>
        </ErrorPage>
    ) : null;
};

export default withAnalyticsContext({ componentName: 'viewTopic' })(withAnalyticsEvents()(HelpCenterTopic));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const List = styled.ol`
    list-style-type: none;
    padding-left: 0;
    margin: 0 ${token('space.negative.300', '-24px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Item = styled.li`
    margin-top: ${token('space.050', '4px')};
`;
